<template>
    <div>
        <TitleBar>
            <template v-slot:title>
              <span>{{ pageTitle }}</span>
              <GekkoButton icon="fa fa-question-circle" variant="link" class="text-blue" v-b-tooltip.click="$gettext('In this foreign VAT report, all VAT charged to foreign customers is reported. You can use the results of this report to do your tax returns in the EU one stop shop. (OSS)')" />
            </template>
            <template v-slot:subtitle>
              <div class="text-blue">
                <translate>Calculated on:</translate>&nbsp;
                <span v-if="report.modified != null">{{ report.modified | formatDate }}</span>
              </div>
            </template>
            <template v-slot:buttons>
                <GekkoButton :text="$gettext('Recalculate')" type="button" variant="outline-primary" class="mr-2" @click="handleSubmit" :disabled="!$route.params.id" />
                <GekkoButton :text="$gettext('All Foreign VAT reports')" type="button" :to="{ name: 'ForeignVATList' }" />
            </template>
        </TitleBar>
        <div class="content">
          <LoadingSpinner v-if="loading" />
          <b-modal id="modal-multi-premium" size="lg" title="Get premium" no-stacking>
              <h4>You need to have a premium account to use this feature!</h4>
          </b-modal>

          <div class="content--inner">
            <b-table
              :class="['foreign-vat-report-table gk-table gk-table--responsive gk-table--headed gk-table--striped']"
              :items="report.report_lines"
              :fields="reportFields"
              :busy="loading"
              show-empty
              table-busy
            >
              <template #empty="">
                {{ $gettext('No items found.') }}
              </template>
              <template #cell(subtotal_amount)="data">
                €{{ data.item.subtotal_amount | formatNumber }}
              </template>
              <template #cell(tax_rate)="data">
                {{ data.item.tax_rate }}%
              </template>
              <template #cell(total_vat)="data">
                €{{ data.item.total_vat | formatNumber }}
              </template>
            </b-table>
          </div>

        </div>
    </div>
</template>
<script>
import { mapGetters } from "vuex";
import GekkoButton from "@/components/GekkoButton";
import TitleBar from "@/components/TitleBar";
import ForeignTaxReportService from "@/store/services/foreigntaxreport.service";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner";



export default {
  name: "ForeignVAT",
  components: {TitleBar, GekkoButton, LoadingSpinner},
  data() {
    return {
      reportFields: [
        {key: 'country_name', label: this.$gettext('Country'), tdClass: "", thClass: ""},
        {
          key: 'subtotal_amount',
          label: this.$gettext('Subtotal amount'),
          tdClass: "tablecol-sm text-right",
          thClass: "text-right"
        },
        {key: 'tax_rate', label: this.$gettext('Tax rate'), tdClass: "tablecol-sm text-right", thClass: "text-right"},
        {
          key: 'total_vat',
          label: this.$gettext('Total VAT'),
          tdClass: "tablecol-sm text-right",
          thClass: "text-right"
        }
      ],
      MONTHS: [
        {text: this.$gettext('Jan'), value: 1},
        {text: this.$gettext('Feb'), value: 2},
        {text: this.$gettext('Mar'), value: 3},
        {text: this.$gettext('Apr'), value: 4},
        {text: this.$gettext('May'), value: 5},
        {text: this.$gettext('Jun'), value: 6},
        {text: this.$gettext('Jul'), value: 7},
        {text: this.$gettext('Aug'), value: 8},
        {text: this.$gettext('Sep'), value: 9},
        {text: this.$gettext('Oct'), value: 10},
        {text: this.$gettext('Nov'), value: 11},
        {text: this.$gettext('Dec'), value: 12}
      ],
      report: {
        created: "2023-12-12T16:52:06.279400+01:00",
        modified: "2023-12-12T16:52:06.279400+01:00",
        report_lines: [
          {"country_name": "Germany", "tax_rate": 21.0, "total_vat": "21.00", "subtotal_amount": "100.00",},
          {"country_name": "Germany", "tax_rate": 19.0, "total_vat": "19.00", "subtotal_amount": "100.00",},
          {"country_name": "Great Britain", "tax_rate": 20, "total_vat": "40.00", "subtotal_amount": "200.00",},
          {"country_name": "Great Britain", "tax_rate": 15, "total_vat": "150.00", "subtotal_amount": "1000.00",},
          {"country_name": "Poland", "tax_rate": 21.0, "total_vat": "63.00", "subtotal_amount": "300.00",},
          {"country_name": "Poland", "tax_rate": 5.0, "total_vat": "5.00", "subtotal_amount": "100.00",}
        ]
      },
      reportsToOverwrite: [],
      startDate: "",
      endDate: "",
      isPremium: false,
      loading: false,
    }
  },
  computed: {
    pageTitle: function () {
      if(this.$route.params.id === undefined){
        return this.$gettext("Sample report");
      }
      var title = this.reportInitialValues.month != null ? this.getMonth(this.reportInitialValues.month, this.MONTHS) : this.reportInitialValues.quarter;
      title += " " + this.reportInitialValues.year;
      return this.$gettext("Foreign tax report for %period%").replace("%period%", title);
    },
    ...mapGetters({
      reportInitialValues: "taxreports/getForeignVATReportInitialValues",
      currentCompany: "main/getCurrentCompany",
    })
  },
  mounted() {
    if (this.$route.params.id !== undefined) {
      this.isPremium = this.$store.getters["getIsPremium"];
      ForeignTaxReportService.get(this.$route.params.id).then((response) => {
        this.report = response.data;
        let report_initial_values = {
          company: this.currentCompany.id,
          year: this.report.year,
          month: this.report.month,
          quarter: this.report.month ? this.calculateQuarter(this.report.month) : this.report.quarter,
        };
        this.$store.commit('taxreports/setForeignVATReportInitialValues', report_initial_values);
      })
    } else {
      let report_initial_values = {
        year: 2023,
        month: null,
        quarter: "Q3",
      };
      this.$store.commit('taxreports/setForeignVATReportInitialValues', report_initial_values);
      this.report.created = "2023-12-12T16:52:06.279400+01:00";
      this.$store.commit('togglePremiumModal', 'web_reports_calculate');
    }
  },
  filters: {
    formatDate: function (value) {
      var options = {year: "numeric", month: "long", day: "numeric"};
      return new Date(value).toLocaleDateString("en-US", options);
    }
  },

  methods: {
    calculateQuarter(month) {
      return 'Q' + Math.floor((month + 2) / 3);
    },
    getMonth(value, months) {
      const result = months.filter(month => month.value === value);
      return result[0].text;
    },
    handleSubmit() {
      this.loading = true; 
  ForeignTaxReportService.recalculate(this.report.id)
    .then((response) => {
      this.report = response.data;
    })
    .catch((error) => {
      console.error('Error while recalculating:', error);
    })
    .finally(() => {
      this.loading = false; 
    });
    }
  }
}
</script>
<style lang="scss">

// table{
//   width: 100%;
// }

// .numbers {
//   width: 25%;
//   text-align: right;
//   font-weight: bold;
// }
// .unsupported{
//   color: #ccc;
// }
// .correction {
//   display: none;
//   &.text-invoiced, &.text-danger { display: inline-block; }
// }

.foreign-vat-report-table  {
  .col-description { font-size: 16px; }
  .col-group { font-size: 14px; }
  &.gk-table--headed {
    thead {
      background: none;
      text-transform: none;
      color: $colorTextBase;
      th {
        font-size: 1rem;
        padding: 1rem 0.75rem;
        font-weight: normal;
        font-family: DINNextLTPro-Medium, sans-serif;
      }
    }
    tbody {
      tr {
        cursor: initial;
      }
    }
  }
}

</style>